import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import bcrypt from 'bcryptjs';
import './SuperAdminDashboard.css';
import './SuperAdminMobileMenu.css';

function AddSchool() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        menuRef.current &&
        toggleRef.current &&
        !menuRef.current.contains(event.target) &&
        !toggleRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);
  
  // School form state
  const [schoolForm, setSchoolForm] = useState({
    schoolName: '',
    email: '',
    password: '',
    confirmPassword: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    principalName: '',
    principalEmail: '',
    schoolType: 'public',
    totalStudents: '',
    totalTeachers: '',
    website: '',
    description: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Country code state
  const [schoolPhoneCountryCode, setSchoolPhoneCountryCode] = useState('+92');

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSchoolForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!schoolForm.schoolName.trim()) errors.schoolName = 'School name is required';
    if (!schoolForm.email.trim()) errors.email = 'Email is required';
    if (!schoolForm.password) errors.password = 'Password is required';
    if (schoolForm.password !== schoolForm.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (!schoolForm.address.trim()) errors.address = 'Address is required';
    if (!schoolForm.city.trim()) errors.city = 'City is required';
    if (!schoolForm.state.trim()) errors.state = 'State is required';
    if (!schoolForm.phone.trim()) errors.phone = 'Phone number is required';
    if (!schoolForm.principalName.trim()) errors.principalName = 'Principal name is required';
    if (!schoolForm.principalEmail.trim()) errors.principalEmail = 'Principal email is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // First, create a Supabase Auth user
      console.log('Creating Supabase Auth user for school:', schoolForm.email);
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: schoolForm.email,
        password: schoolForm.password,
        options: {
          data: {
            user_type: 'school',
            school_name: schoolForm.schoolName
          }
        }
      });

      if (authError) {
        console.error('Supabase Auth error:', authError);
        if (authError.message.includes('already registered')) {
          setErrorMessage('A school with this email already exists. Please use a different email.');
        } else {
          setErrorMessage('Failed to create school account. Please try again.');
        }
        setShowErrorPopup(true);
        setIsSubmitting(false);
        return;
      }

      console.log('Supabase Auth user created successfully:', authData);

      // Prepare data for Supabase schools table
      const schoolData = {
        school_name: schoolForm.schoolName,
        school_type: schoolForm.schoolType,
        website: schoolForm.website || null,
        email: schoolForm.email,
        auth_user_id: authData.user.id, // Store the Supabase Auth user ID
        address: schoolForm.address,
        city: schoolForm.city,
        state: schoolForm.state,
        zip_code: schoolForm.zipCode || null,
        phone: schoolForm.phone.trim() ? `${schoolPhoneCountryCode} ${schoolForm.phone.trim()}` : '',
        principal_name: schoolForm.principalName,
        principal_email: schoolForm.principalEmail,
        total_students: schoolForm.totalStudents ? parseInt(schoolForm.totalStudents) : 0,
        total_teachers: schoolForm.totalTeachers ? parseInt(schoolForm.totalTeachers) : 0,
        description: schoolForm.description || null,
        is_active: true
      };
      
      console.log('Saving school data to Supabase:', schoolData);
      
      // Test Supabase connection first
      console.log('Testing Supabase connection...');
      const { data: testData, error: testError } = await supabase
        .from('schools')
        .select('count')
        .limit(1);
      
      if (testError) {
        console.error('Supabase connection test failed:', testError);
        setErrorMessage(`Database connection error: ${testError.message}`);
        setShowErrorPopup(true);
        setIsSubmitting(false);
        return;
      }
      
      console.log('Supabase connection test successful');
      
      // Save to Supabase
      const { data, error } = await supabase
        .from('schools')
        .insert([schoolData])
        .select();
      
      if (error) {
        console.error('Supabase insert error:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }
      
      console.log('School saved successfully:', data);
      
      // Store success data
      setSuccessData({
        schoolName: schoolForm.schoolName,
        email: schoolForm.email,
        schoolType: schoolForm.schoolType
      });
      
      // Reset form
      setSchoolForm({
        schoolName: '',
        email: '',
        password: '',
        confirmPassword: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
        principalName: '',
        principalEmail: '',
        schoolType: 'public',
        totalStudents: '',
        totalTeachers: '',
        website: '',
        description: ''
      });
      
      // Reset password visibility
      setShowPassword(false);
      setShowConfirmPassword(false);
      
      // Show success popup
      setShowSuccessPopup(true);
      
    } catch (error) {
      console.error('Error adding school:', error);
      
      // Handle specific Supabase errors
      let errorMsg = '';
      if (error.code === '23505') {
        errorMsg = 'A school with this email already exists. Please use a different email.';
      } else if (error.code === '23514') {
        errorMsg = 'Invalid data provided. Please check all required fields.';
      } else if (error.code === 'PGRST301') {
        errorMsg = 'The "schools" table does not exist. Please create the table first.';
      } else if (error.code === '42501') {
        errorMsg = 'Permission denied. Check your Row Level Security policies.';
      } else if (error.message && error.message.includes('relation "schools" does not exist')) {
        errorMsg = 'The "schools" table does not exist. Please create it in Supabase first.';
      } else {
        errorMsg = error.message || 'Failed to add school. Please try again.';
        if (error.code) {
          errorMsg += ` (Error Code: ${error.code})`;
        }
      }
      
      setErrorMessage(errorMsg);
      setShowErrorPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Sign out from Supabase if logged in
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.auth.signOut();
      }
      
      // Clear localStorage
      localStorage.removeItem('isSuperAdminAuthenticated');
      
      // Redirect to home page
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear localStorage and redirect even if there's an error
      localStorage.removeItem('isSuperAdminAuthenticated');
      navigate('/');
    }
  };

  return (
    <div className="super-admin-dashboard">
      {/* Mobile Header */}
      <div className="mobile-header">
        <div className="mobile-header-left">
          <span className="mobile-header-text">Super Admin</span>
        </div>
        <button 
          ref={toggleRef}
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Sidebar - Mobile Dropdown Menu */}
      <div ref={menuRef} className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <img 
            src="https://gjamvfwvqlizginqdfhd.supabase.co/storage/v1/object/public/transport-uploads/WhatsApp%20Image%202025-11-21%20at%2011.57.28%20AM%20(1).jpeg" 
            alt="Nexy App Logo" 
            className="sidebar-logo"
          />
        </div>
        
        <nav className="sidebar-nav">
          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/dashboard');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Dashboard</span>
          </button>
          
          <button
            className="nav-item active"
            onClick={() => {
              navigate('/superadmin/add-school');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Add School</span>
          </button>
          
          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/schools');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Schools</span>
          </button>

          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/terms-and-conditions');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Terms & Conditions</span>
          </button>

          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/privacy-policy');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Privacy Policy / EULA</span>
          </button>
        </nav>
        
        <div className="sidebar-spacer"></div>
        
        <div className="sidebar-footer">
          <button 
            className="logout-btn" 
            onClick={() => {
              handleLogout();
              setMobileMenuOpen(false);
            }}
          >
            <span>Logout</span>
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <h1>Add New School</h1>
          <div className="user-info">
            <span>Super Admin</span>
            <div className="user-avatar">👑</div>
          </div>
        </div>
        
        <div className="content-body">
          <div className="add-school-content">
            <div className="school-form-container">
              <form onSubmit={handleSubmit} className="school-form">
                <div className="form-section">
                  <h3>Basic Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="schoolName">School Name <span className="asterisk">*</span></label>
                      <input
                        type="text"
                        id="schoolName"
                        name="schoolName"
                        value={schoolForm.schoolName}
                        onChange={handleInputChange}
                        className={formErrors.schoolName ? 'error' : ''}
                        placeholder="Enter school name"
                      />
                      {formErrors.schoolName && <span className="error-text">{formErrors.schoolName}</span>}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="schoolType">School Type</label>
                      <select
                        id="schoolType"
                        name="schoolType"
                        value={schoolForm.schoolType}
                        onChange={handleInputChange}
                      >
                        <option value="public">Public</option>
                        <option value="private">Private</option>
                        <option value="charter">Charter</option>
                        <option value="international">International</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="website">Website</label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={schoolForm.website}
                      onChange={handleInputChange}
                      placeholder="https://www.schoolname.edu"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3>Login Credentials</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">School Email <span className="asterisk">*</span></label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={schoolForm.email}
                        onChange={handleInputChange}
                        className={formErrors.email ? 'error' : ''}
                        placeholder="admin@schoolname.edu"
                      />
                      {formErrors.email && <span className="error-text">{formErrors.email}</span>}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="password">Password <span className="asterisk">*</span></label>
                      <div className="password-input-wrapper" style={{ position: 'relative', width: '100%' }}>
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          name="password"
                          value={schoolForm.password}
                          onChange={handleInputChange}
                          className={formErrors.password ? 'error' : ''}
                          placeholder="Enter password"
                          style={{ 
                            paddingRight: '40px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                        <button
                          type="button"
                          className="password-toggle-icon"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px',
                            width: '24px',
                            height: '24px',
                            zIndex: 10
                          }}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? '🙈' : '👁️'}
                        </button>
                      </div>
                      {formErrors.password && <span className="error-text">{formErrors.password}</span>}
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="confirmPassword">Confirm Password <span className="asterisk">*</span></label>
                      <div className="password-input-wrapper" style={{ position: 'relative', width: '100%' }}>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          id="confirmPassword"
                          name="confirmPassword"
                          value={schoolForm.confirmPassword}
                          onChange={handleInputChange}
                          className={formErrors.confirmPassword ? 'error' : ''}
                          placeholder="Confirm password"
                          style={{ 
                            paddingRight: '40px',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                        <button
                          type="button"
                          className="password-toggle-icon"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px',
                            width: '24px',
                            height: '24px',
                            zIndex: 10
                          }}
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          {showConfirmPassword ? '🙈' : '👁️'}
                        </button>
                      </div>
                      {formErrors.confirmPassword && <span className="error-text">{formErrors.confirmPassword}</span>}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Contact Information</h3>
                  <div className="form-group">
                    <label htmlFor="address">Address <span className="asterisk">*</span></label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={schoolForm.address}
                      onChange={handleInputChange}
                      className={formErrors.address ? 'error' : ''}
                      placeholder="Street address"
                    />
                    {formErrors.address && <span className="error-text">{formErrors.address}</span>}
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">City <span className="asterisk">*</span></label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={schoolForm.city}
                        onChange={handleInputChange}
                        className={formErrors.city ? 'error' : ''}
                        placeholder="City"
                      />
                      {formErrors.city && <span className="error-text">{formErrors.city}</span>}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="state">State <span className="asterisk">*</span></label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={schoolForm.state}
                        onChange={handleInputChange}
                        className={formErrors.state ? 'error' : ''}
                        placeholder="State"
                      />
                      {formErrors.state && <span className="error-text">{formErrors.state}</span>}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="zipCode">ZIP Code</label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={schoolForm.zipCode}
                        onChange={handleInputChange}
                        placeholder="12345"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number <span className="asterisk">*</span></label>
                      <div className="phone-input-wrapper">
                        <select
                          className="country-code-select"
                          value={schoolPhoneCountryCode}
                          onChange={(e) => setSchoolPhoneCountryCode(e.target.value)}
                        >
                          <option value="+1">🇺🇸 +1 (US/CA)</option>
                          <option value="+44">🇬🇧 +44 (UK)</option>
                          <option value="+91">🇮🇳 +91 (India)</option>
                          <option value="+92">🇵🇰 +92 (Pakistan)</option>
                          <option value="+971">🇦🇪 +971 (UAE)</option>
                          <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                          <option value="+974">🇶🇦 +974 (Qatar)</option>
                          <option value="+965">🇰🇼 +965 (Kuwait)</option>
                          <option value="+973">🇧🇭 +973 (Bahrain)</option>
                          <option value="+968">🇴🇲 +968 (Oman)</option>
                          <option value="+961">🇱🇧 +961 (Lebanon)</option>
                          <option value="+962">🇯🇴 +962 (Jordan)</option>
                          <option value="+20">🇪🇬 +20 (Egypt)</option>
                          <option value="+27">🇿🇦 +27 (South Africa)</option>
                          <option value="+61">🇦🇺 +61 (Australia)</option>
                          <option value="+64">🇳🇿 +64 (New Zealand)</option>
                          <option value="+86">🇨🇳 +86 (China)</option>
                          <option value="+81">🇯🇵 +81 (Japan)</option>
                          <option value="+82">🇰🇷 +82 (South Korea)</option>
                          <option value="+65">🇸🇬 +65 (Singapore)</option>
                          <option value="+60">🇲🇾 +60 (Malaysia)</option>
                          <option value="+66">🇹🇭 +66 (Thailand)</option>
                          <option value="+62">🇮🇩 +62 (Indonesia)</option>
                          <option value="+63">🇵🇭 +63 (Philippines)</option>
                          <option value="+84">🇻🇳 +84 (Vietnam)</option>
                          <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                          <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                          <option value="+977">🇳🇵 +977 (Nepal)</option>
                          <option value="+33">🇫🇷 +33 (France)</option>
                          <option value="+49">🇩🇪 +49 (Germany)</option>
                          <option value="+39">🇮🇹 +39 (Italy)</option>
                          <option value="+34">🇪🇸 +34 (Spain)</option>
                          <option value="+31">🇳🇱 +31 (Netherlands)</option>
                          <option value="+32">🇧🇪 +32 (Belgium)</option>
                          <option value="+41">🇨🇭 +41 (Switzerland)</option>
                          <option value="+43">🇦🇹 +43 (Austria)</option>
                          <option value="+46">🇸🇪 +46 (Sweden)</option>
                          <option value="+47">🇳🇴 +47 (Norway)</option>
                          <option value="+45">🇩🇰 +45 (Denmark)</option>
                          <option value="+358">🇫🇮 +358 (Finland)</option>
                          <option value="+7">🇷🇺 +7 (Russia)</option>
                          <option value="+90">🇹🇷 +90 (Turkey)</option>
                          <option value="+52">🇲🇽 +52 (Mexico)</option>
                          <option value="+55">🇧🇷 +55 (Brazil)</option>
                          <option value="+54">🇦🇷 +54 (Argentina)</option>
                          <option value="+57">🇨🇴 +57 (Colombia)</option>
                          <option value="+234">🇳🇬 +234 (Nigeria)</option>
                          <option value="+254">🇰🇪 +254 (Kenya)</option>
                          <option value="+212">🇲🇦 +212 (Morocco)</option>
                          <option value="+972">🇮🇱 +972 (Israel)</option>
                          <option value="+98">🇮🇷 +98 (Iran)</option>
                          <option value="+964">🇮🇶 +964 (Iraq)</option>
                          <option value="+967">🇾🇪 +967 (Yemen)</option>
                          <option value="+963">🇸🇾 +963 (Syria)</option>
                        </select>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={schoolForm.phone}
                          onChange={handleInputChange}
                          className={formErrors.phone ? 'error' : ''}
                          placeholder="e.g. 300 1234567"
                        />
                      </div>
                      {formErrors.phone && <span className="error-text">{formErrors.phone}</span>}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Principal Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="principalName">Principal Name <span className="asterisk">*</span></label>
                      <input
                        type="text"
                        id="principalName"
                        name="principalName"
                        value={schoolForm.principalName}
                        onChange={handleInputChange}
                        className={formErrors.principalName ? 'error' : ''}
                        placeholder="Full name"
                      />
                      {formErrors.principalName && <span className="error-text">{formErrors.principalName}</span>}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="principalEmail">Principal Email <span className="asterisk">*</span></label>
                      <input
                        type="email"
                        id="principalEmail"
                        name="principalEmail"
                        value={schoolForm.principalEmail}
                        onChange={handleInputChange}
                        className={formErrors.principalEmail ? 'error' : ''}
                        placeholder="principal@schoolname.edu"
                      />
                      {formErrors.principalEmail && <span className="error-text">{formErrors.principalEmail}</span>}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>School Statistics</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="totalStudents">Total Students</label>
                      <input
                        type="number"
                        id="totalStudents"
                        name="totalStudents"
                        value={schoolForm.totalStudents}
                        onChange={handleInputChange}
                        placeholder="e.g., 500"
                        min="0"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="totalTeachers">Total Teachers</label>
                      <input
                        type="number"
                        id="totalTeachers"
                        name="totalTeachers"
                        value={schoolForm.totalTeachers}
                        onChange={handleInputChange}
                        placeholder="e.g., 25"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Additional Information</h3>
                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={schoolForm.description}
                      onChange={handleInputChange}
                      placeholder="Brief description of the school..."
                      rows="4"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => navigate('/superadmin/dashboard')}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn" disabled={isSubmitting} style={{ backgroundColor: '#FF8C42', color: 'white' }}>
                    {isSubmitting ? 'Adding School...' : 'Add School'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && successData && (
        <div 
          className="success-popup-overlay" 
          onClick={() => {
            setShowSuccessPopup(false);
            navigate('/superadmin/schools');
          }}
        >
          <div 
            className="success-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="success-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#10B981" opacity="0.2"/>
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-popup-title">School Added Successfully!</h2>
            <div className="success-popup-details">
              <div className="success-detail-item">
                <span className="success-detail-label">School Name:</span>
                <span className="success-detail-value">{successData.schoolName}</span>
              </div>
              <div className="success-detail-item">
                <span className="success-detail-label">Email:</span>
                <span className="success-detail-value">{successData.email}</span>
              </div>
              <div className="success-detail-item">
                <span className="success-detail-label">School Type:</span>
                <span className="success-detail-value">{successData.schoolType.charAt(0).toUpperCase() + successData.schoolType.slice(1)}</span>
              </div>
            </div>
            <div className="success-popup-actions">
              <button 
                className="success-popup-btn"
                onClick={() => {
                  setShowSuccessPopup(false);
                  navigate('/superadmin/schools');
                }}
              >
                View Schools
              </button>
              <button 
                className="success-popup-btn secondary"
                onClick={() => {
                  setShowSuccessPopup(false);
                }}
              >
                Add Another
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showErrorPopup && errorMessage && (
        <div 
          className="error-popup-overlay" 
          onClick={() => setShowErrorPopup(false)}
        >
          <div 
            className="error-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="error-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#EF4444" opacity="0.2"/>
                <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="error-popup-title">Error Adding School</h2>
            <div className="error-popup-message">
              <p>{errorMessage}</p>
            </div>
            <div className="error-popup-actions">
              <button 
                className="error-popup-btn"
                onClick={() => setShowErrorPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddSchool;

